/**
 * @fileoverview Shared hook for pure-state canvas mutation callbacks.
 * @module ui-admin/hooks
 *
 * Responsibilities:
 * - Provide a single reusable implementation of all canvas callbacks whose
 *   logic is identical across every host app (tenant-web, system-web).
 * - Eliminate page-level duplication in violation of the component-first mandate.
 *
 * Dependencies:
 * - React (useCallback)
 * - TemplateBuilderForm, TemplateBuilderTemplate, FormRef from templateTypes
 *
 * Architecture:
 * - Local-first: all callbacks mutate only in-memory state (forms Map +
 *   template formRefs). Persistence is the host page's responsibility via
 *   its own Dexie-backed services — this hook never calls APIs directly.
 * - Host pages inject { template, patchTemplate, setForms }; receive the
 *   five shared callbacks in return.
 *
 * Notes:
 * - App-specific callbacks (onUpdateForm, onAddFormFromLibrary, onAddForm)
 *   remain in host pages because their service calls differ per app.
 *   Extract them here only when their logic converges fully.
 */

import { useCallback, useEffect, useRef } from 'react';
import type { TemplateBuilderForm, TemplateBuilderTemplate, FormRef } from '../types/templateTypes';

// ---------------------------------------------------------------------------
// Input contract
// ---------------------------------------------------------------------------

/**
 * @contract UseTemplateCanvasCallbacksInput
 *
 * Purpose:
 * - Injects the host's local state so the hook can produce correctly-scoped
 *   callback functions without owning the state itself.
 *
 * Fields:
 * - template: current template record (read-only by the hook).
 * - patchTemplate: host's partial-update function for template.
 * - setForms: host's forms-Map setter.
 *
 * Rules:
 * - template must be the live reference so closures capture correct formRefs.
 */
export interface UseTemplateCanvasCallbacksInput {
  template: TemplateBuilderTemplate;
  patchTemplate: (patch: Partial<TemplateBuilderTemplate>) => void;
  setForms: React.Dispatch<React.SetStateAction<Map<string, TemplateBuilderForm>>>;
}

// ---------------------------------------------------------------------------
// Return contract
// ---------------------------------------------------------------------------

/**
 * @contract UseTemplateCanvasCallbacksResult
 *
 * Purpose:
 * - Typed surface returned to the host page so prop wiring is explicit and
 *   discoverable.
 *
 * Fields:
 * - onNewFormAdded: notify host that TemplateCanvas created a new blank form.
 * - onAddModuleForm: add a module form returned by ModulePickerModal.
 * - onRemoveFormRef: remove a form reference from the template.
 * - onReorderForms: replace the full formRefs array after a drag-reorder.
 * - onUpgradeFormRef: bump a single form ref to a newer form version.
 *
 * Sync:
 * - All mutations are local-only; host triggers background sync separately.
 */
export interface UseTemplateCanvasCallbacksResult {
  onNewFormAdded: (form: TemplateBuilderForm) => void;
  onAddModuleForm: (form: TemplateBuilderForm) => void;
  onRemoveFormRef: (formId: string) => void;
  onReorderForms: (newRefs: FormRef[]) => void;
  onUpgradeFormRef: (formId: string, toVersion: number) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @hook useTemplateCanvasCallbacks
 *
 * Purpose:
 * - Single reusable source for the five pure-state canvas mutation callbacks
 *   shared by every inspection template editor host (tenant-web, system-web).
 *
 * Returns:
 * - Five memoised callbacks; see UseTemplateCanvasCallbacksResult.
 *
 * Side Effects:
 * - None. Every callback mutates only the in-memory forms Map and template
 *   formRefs via the injected setters.
 *
 * Offline Behavior:
 * - Fully offline-safe: operates on local state only.
 *
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - Callbacks call patchTemplate / setForms directly; host persists via Dexie.
 *
 * Sync Strategy:
 * - Host is responsible for triggering background sync after state changes.
 *
 * Conflict Resolution:
 * - NOT REQUIRED — hook only manages optimistic local state.
 */
export function useTemplateCanvasCallbacks({
  template,
  patchTemplate,
  setForms,
}: UseTemplateCanvasCallbacksInput): UseTemplateCanvasCallbacksResult {
  const latestTemplateRef = useRef(template);
  useEffect(() => {
    latestTemplateRef.current = template;
  }, [template]);

  /**
   * Called by TemplateCanvas after its internal New Form dialog creates a form.
   * Adds the form to the local map and appends a standard ref to the template.
   */
  const onNewFormAdded = useCallback(
    (form: TemplateBuilderForm) => {
      setForms((prev) => new Map(prev).set(form._id, form));
      const latestTemplate = latestTemplateRef.current;
      patchTemplate({
        formRefs: [
          ...latestTemplate.formRefs,
          {
            formId: form._id,
            formVersion: (form as any).version ?? 1,
            order: latestTemplate.formRefs.length,
            optional: false,
            formKind: (form as any).formKind ?? 'standard',
          } as FormRef,
        ],
      });
    },
    [patchTemplate, setForms],
  );

  /**
   * Called when a module form (Contacts, Violations, etc.) is confirmed in
   * ModulePickerModal and created by TemplateCanvas's onAddModuleForm path.
   */
  const onAddModuleForm = useCallback(
    (form: TemplateBuilderForm) => {
      setForms((prev) => new Map(prev).set(form._id, form));
      const latestTemplate = latestTemplateRef.current;
      patchTemplate({
        formRefs: [
          ...latestTemplate.formRefs,
          {
            formId: form._id,
            formVersion: form.version,
            order: latestTemplate.formRefs.length,
            optional: false,
            formKind: 'module',
            moduleConfig: (form as any).moduleConfig,
          } as FormRef,
        ],
      });
    },
    [patchTemplate, setForms],
  );

  /**
   * Removes a form reference from the template; does not delete the form record.
   * Caller re-indexes order after removal if required.
   */
  const onRemoveFormRef = useCallback(
    (formId: string) => {
      patchTemplate({
        formRefs: template.formRefs
          .filter((r) => r.formId !== formId)
          .map((r, i) => ({ ...r, order: i + 1 })),
      });
    },
    [template, patchTemplate],
  );

  /**
   * Replaces the full formRefs array after a drag-reorder operation in the canvas.
   */
  const onReorderForms = useCallback(
    (newRefs: FormRef[]) => {
      patchTemplate({ formRefs: newRefs });
    },
    [patchTemplate],
  );

  /**
   * Bumps the stored formVersion for a single ref when the user accepts an
   * upgrade prompt in the canvas.
   */
  const onUpgradeFormRef = useCallback(
    (formId: string, toVersion: number) => {
      patchTemplate({
        formRefs: template.formRefs.map((r) =>
          r.formId === formId ? { ...r, formVersion: toVersion } : r,
        ),
      });
    },
    [template, patchTemplate],
  );

  return {
    onNewFormAdded,
    onAddModuleForm,
    onRemoveFormRef,
    onReorderForms,
    onUpgradeFormRef,
  };
}
